import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

/**
 * Utdelningsomgångar: utdelarnas svar (kan dela ja/nej + hemma från) hör till en omgång.
 * "Nollställ till nästa utdelning" = starta en ny omgång; de gamla svaren finns kvar.
 */
export interface DistributionRound {
    id: string;
    municipalityPolygonId: string;
    name: string;
    campaignId?: string;
    startedAt: string;
    closedAt?: string;
    yesCount: number;
    noCount: number;
}

export interface DistributionRoundList {
    active?: DistributionRound;
    history: DistributionRound[];
}

export interface DistributionResponse {
    kretsMemberId: string;
    kretsMemberName: string;
    /** null/undefined = inget svar. */
    canDeliver?: boolean | null;
    homeFromDate?: string;
    respondedAt?: string;
}

export const fetchDistributionRounds = async (municipalityId: string): Promise<DistributionRoundList> => {
    const { data } = await apiClient.get<DistributionRoundList>("/distribution-rounds", {
        params: { municipalityId }
    });
    return data;
};

export const createDistributionRound = async (request: {
    municipalityPolygonId: string;
    name: string;
    campaignId?: string;
}): Promise<DistributionRound> => {
    const { data } = await apiClient.post<DistributionRound>("/distribution-rounds", request);
    return data;
};

export const fetchDistributionResponses = async (roundId: string): Promise<DistributionResponse[]> => {
    const { data } = await apiClient.get<DistributionResponse[]>(`/distribution-rounds/${roundId}/responses`);
    return data;
};

export const useDistributionRounds = (municipalityId: string) =>
    useQuery({
        queryKey: ["distribution-rounds", municipalityId],
        queryFn: () => fetchDistributionRounds(municipalityId),
        enabled: !!municipalityId,
        staleTime: 60_000
    });

export const useDistributionResponses = (roundId: string | undefined) =>
    useQuery({
        queryKey: ["distribution-rounds", "responses", roundId],
        queryFn: () => fetchDistributionResponses(roundId!),
        enabled: !!roundId,
        staleTime: 30_000
    });

export const useCreateDistributionRound = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createDistributionRound,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["distribution-rounds"] });
        }
    });
};
